import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PersonDetail, PersonSummary } from '@blesk/contracts';
import { apiGet, apiPost } from '../../shared/api';
import { useProjectOptions } from '../../shared/options';
import { usePagedList } from '../../shared/lists';
import { CHANNEL_LABEL, LINK_STATUS_LABEL, PERSON_STATUS_LABEL } from '../../shared/labels';
import { formatDateInProjectTz } from '../../shared/format';
import { useHasPermission, useSession } from '../../shared/session';
import { useUrlFilter } from '../../shared/filters';
import { PermissionGate } from '../shell/PermissionGate';
import { Dialog, EmptyRow, ErrorNote, Pager, orDash, useConfirm } from '../shell/ui';

/** §7.2, §7.3, §13 ТЗ. Раздел «Администраторы заказчика». */
export function PeoplePage() {
  return (
    <PermissionGate permission="people.read">
      <PeopleInner />
    </PermissionGate>
  );
}

function PeopleInner() {
  const queryClient = useQueryClient();
  const [projectId, setProjectId] = useUrlFilter('projectId');
  const [status, setStatus] = useUrlFilter('status');
  const [linkStatus, setLinkStatus] = useUrlFilter('linkStatus');
  const [search, setSearch] = useUrlFilter('search');
  const [duplicatesOnly, setDuplicatesOnly] = useUrlFilter('duplicatesOnly');
  const [openId, setOpenId] = useState<string | null>(null);

  const projects = useProjectOptions();
  const list = usePagedList<PersonSummary>('people', '/api/people', {
    projectId,
    status,
    linkStatus,
    search,
    duplicatesOnly: duplicatesOnly || undefined,
  });

  return (
    <>
      <h1>Администраторы заказчика</h1>
      <p className="muted">
        Слияние дублей — только вручную; автоматического объединения по ФИО нет.
      </p>

      <div className="toolbar">
        {/* Отбор по объекту принимает сервер, и сюда ведёт ссылка из «Объектов»:
            без поля в панели он был бы применён невидимо */}
        <label>
          Объект
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Все</option>
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Статус
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Все</option>
            {Object.entries(PERSON_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Статус связи
          <select value={linkStatus} onChange={(e) => setLinkStatus(e.target.value)}>
            <option value="">Все</option>
            {Object.entries(LINK_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Поиск
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ФИО, телефон, почта"
          />
        </label>
        <label>
          <span>Только возможные дубли</span>
          <input
            type="checkbox"
            checked={duplicatesOnly === 'true'}
            onChange={(e) => setDuplicatesOnly(e.target.checked ? 'true' : '')}
          />
        </label>
      </div>
      <ErrorNote error={list.error ?? projects.error} />

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>ФИО</th>
              <th>Телефон</th>
              <th>Почта</th>
              <th>Статус</th>
              <th>Связи с объектами</th>
              <th>Согласие</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {list.items.length === 0 && <EmptyRow colSpan={7} loading={list.isPending} />}
            {list.items.map((person) => (
              <tr key={person.id}>
                <td>
                  {person.fullName}
                  {person.duplicateOfId && (
                    <span className="badge badge--warn cell-mark__badge">дубль?</span>
                  )}
                </td>
                <td>{orDash(person.phone)}</td>
                <td>{orDash(person.email)}</td>
                <td>{PERSON_STATUS_LABEL[person.status]}</td>
                <td>
                  {/* Действующая связь — норма, и подпись «(Действует)» у каждой
                      строки только прячет ту единственную, что отозвана */}
                  {person.links.length === 0
                    ? '—'
                    : person.links
                        .map((link) =>
                          link.status === 'ACTIVE'
                            ? link.projectName
                            : `${link.projectName} (${LINK_STATUS_LABEL[link.status]})`,
                        )
                        .join(', ')}
                </td>
                <td>{person.consentAt ? 'есть' : 'нет'}</td>
                <td>
                  <div className="row-actions">
                    <button type="button" onClick={() => setOpenId(person.id)}>
                      Карточка
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={list.page} perPage={list.perPage} total={list.total} onPage={list.setPage} />

      {openId && (
        <PersonCard
          personId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['people'] })}
        />
      )}
    </>
  );
}

/** Карточка персоны: связи, каналы, кандидаты на дубли, блокировка. */
function PersonCard({
  personId,
  onClose,
  onChanged,
}: {
  personId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const session = useSession();
  const canRevoke = useHasPermission('people.revoke');
  const canWrite = useHasPermission('people.write');
  const confirmation = useConfirm();
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ['people', 'detail', personId],
    queryFn: () => apiGet<PersonDetail>(`/api/people/${personId}`),
  });

  const refresh = () => {
    onChanged();
    void queryClient.invalidateQueries({ queryKey: ['people', 'detail', personId] });
  };

  const revokeLink = useMutation({
    mutationFn: (linkId: string) => apiPost(`/api/people/links/${linkId}/revoke`, {}),
    onSuccess: refresh,
  });
  const block = useMutation({
    mutationFn: () => apiPost(`/api/people/${personId}/block`, {}),
    onSuccess: refresh,
  });
  const unblock = useMutation({
    mutationFn: () => apiPost(`/api/people/${personId}/unblock`),
    onSuccess: refresh,
  });
  const merge = useMutation({
    mutationFn: (targetPersonId: string) =>
      apiPost(`/api/people/${personId}/merge`, { targetPersonId }),
    onSuccess: refresh,
  });

  const person = detail.data;

  return (
    <Dialog title={person ? person.fullName : 'Карточка администратора'} onClose={onClose}>
      {!person && <p className="muted">{detail.isPending ? 'Загрузка…' : ''}</p>}
      <ErrorNote
        error={detail.error ?? revokeLink.error ?? block.error ?? unblock.error ?? merge.error}
      />
      {person && (
        <>
          <dl className="detail">
            <dt>Телефон</dt>
            <dd>{orDash(person.phone)}</dd>
            <dt>Почта</dt>
            <dd>{orDash(person.email)}</dd>
            <dt>Статус</dt>
            <dd>{PERSON_STATUS_LABEL[person.status]}</dd>
            <dt>Согласие на обработку ПДн</dt>
            <dd>
              {person.consentAt
                ? formatDateInProjectTz(person.consentAt, session.defaultTimezone)
                : 'не зафиксировано'}
            </dd>
            <dt>Каналы</dt>
            <dd>
              {person.identities.length === 0
                ? '—'
                : person.identities
                    .map((identity) =>
                      identity.displayName
                        ? `${CHANNEL_LABEL[identity.channel]} (${identity.displayName})`
                        : CHANNEL_LABEL[identity.channel],
                    )
                    .join(', ')}
            </dd>
          </dl>

          <h3>Связи с объектами</h3>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Объект</th>
                  <th>Статус</th>
                  <th>Канал</th>
                  {canRevoke && <th></th>}
                </tr>
              </thead>
              <tbody>
                {person.links.map((link) => (
                  <tr key={link.id}>
                    <td>{link.projectName}</td>
                    <td>{LINK_STATUS_LABEL[link.status]}</td>
                    <td>{link.preferredChannel ? CHANNEL_LABEL[link.preferredChannel] : '—'}</td>
                    {canRevoke && (
                      <td>
                        {link.status === 'ACTIVE' && (
                          <button
                            type="button"
                            className="btn-danger"
                            disabled={revokeLink.isPending}
                            onClick={() =>
                              confirmation.ask({
                                title: 'Отзыв связи с объектом',
                                question: `Отозвать связь «${person.fullName}» с объектом «${link.projectName}»?`,
                                warning:
                                  'Опросы по этой связи перестанут приходить. История ответов сохраняется.',
                                verb: 'Отозвать',
                                danger: true,
                                onConfirm: () => revokeLink.mutate(link.id),
                              })
                            }
                          >
                            Отозвать
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {canWrite && person.duplicateCandidates.length > 0 && (
            <div className="section-block">
              <h3>Возможные дубли</h3>
              <p className="muted">
                Совпадение по телефону или почте. Слияние переносит связи и каналы в выбранную
                персону; текущая перестаёт участвовать в работе. Решение принимает сотрудник.
              </p>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>ФИО</th>
                      <th>Совпало</th>
                      <th>Объекты</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {person.duplicateCandidates.map((candidate) => (
                      <tr key={candidate.id}>
                        <td>{candidate.fullName}</td>
                        <td>
                          {candidate.matchedBy
                            .map((field) => (field === 'PHONE' ? 'телефон' : 'почта'))
                            .join(', ')}
                        </td>
                        <td>{candidate.projectNames.join(', ') || '—'}</td>
                        <td>
                          <button
                            type="button"
                            disabled={merge.isPending}
                            onClick={() =>
                              confirmation.ask({
                                title: 'Слияние персон',
                                question: `Слить «${person.fullName}» в «${candidate.fullName}»?`,
                                warning:
                                  'Связи и каналы переедут в выбранную персону. Операция необратима — разделить их обратно нельзя.',
                                verb: 'Слить',
                                danger: true,
                                onConfirm: () => merge.mutate(candidate.id),
                              })
                            }
                          >
                            Слить в эту персону
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {canWrite && (
            <div className="form__actions">
              {person.status === 'ACTIVE' ? (
                <button
                  type="button"
                  className="btn-danger"
                  disabled={block.isPending}
                  onClick={() =>
                    confirmation.ask({
                      title: 'Блокировка администратора',
                      question: `Заблокировать «${person.fullName}»?`,
                      warning:
                        'Опросы и обращения от него приниматься не будут; связи сохраняются.',
                      verb: 'Заблокировать',
                      danger: true,
                      onConfirm: () => block.mutate(),
                    })
                  }
                >
                  Заблокировать
                </button>
              ) : (
                <button type="button" disabled={unblock.isPending} onClick={() => unblock.mutate()}>
                  Разблокировать
                </button>
              )}
            </div>
          )}
        </>
      )}
      {confirmation.dialog}
    </Dialog>
  );
}
